import { Label, Listbox, ListboxButton } from '@headlessui/react'
import type { Placement } from '@ui-components/Tooltip'
import classNames from 'classnames'
import toLower from 'ramda/src/toLower'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import { usePopper } from 'react-popper'
import {
  getSearchFieldName,
  SearchField
} from '../entityControl/buildSearchFilter'
import ControlDataLoader, { Noop } from '../entityControl/ControlDataLoader'
import { FormError } from '../FormError'
import { useInputFillAnimation } from '../Input/useInputFillAnimation'
import TruncatedTooltipText from '../Tooltip/TruncatedTooltipText'
import DropdownButton from './Button/DropdownButton'
import DropdownList from './List/DropdownList'

export interface IDropdown {
  id?: string
  title?: string
  name?: string
  placeholder?: string
  textField?: string
  valueField?: string
  className?: string
  data?: any[]
  loading?: boolean
  disabled?: boolean
  defaultOpen?: boolean
  inline?: boolean
  value?: any
  required?: boolean
  cellEditor?: boolean
  listClass?: string
  paddingClass?: string
  itemBorderClass?: string
  popperPlacement?: Placement
  DataLoader?: any
  queryVariables?: Record<any, any>
  allowCreate?: 'onFilter' | false
  createMinLength?: number
  createMaxLength?: number
  allowSearch?: boolean
  searchFields?: SearchField[]
  dataParser?: (data: any[]) => any[]
  onCreate?: (item: any) => void
  error?: string | string[] | any
  onChange: (value: any) => void
  onBlur?: (e?: any) => void
  renderClearButton?: ((onClear: () => void) => React.ReactElement) | false
  renderListTop?: () => React.ReactElement
  renderItemSeparator?: ({
    prevItem,
    nextItem
  }: {
    prevItem: Record<string, any>
    nextItem: Record<string, any>
  }) => React.ReactElement
  renderItem?: ({
    item,
    inButton
  }: {
    item: any
    inButton: boolean
  }) => React.ReactElement
  renderButtonContent?: ({
    item,
    placeholder
  }: {
    item?: any
    placeholder?: string
  }) => React.ReactElement
  buttonElement?: (props: DropdownButtonInjectedProps) => React.ReactElement
  labelFontSize?: string
  labelClass?: string
  bgColorClass?: string
  renderInPortal?: boolean
  index?: number
}

export interface DropdownHandle {
  triggerFillAnimation: () => void
}

export interface DropdownButtonInjectedProps {
  open: boolean
  loading?: boolean
  fillAnimationClassName?: string
  onFillAnimationEnd?: () => void
  popperPlacement?: Placement
  setButtonElement: (
    element:
      | HTMLButtonElement
      | null
      | ((ref: HTMLButtonElement | null) => HTMLButtonElement | null)
  ) => void
}

export interface DropdownButtonRenderProps extends DropdownButtonInjectedProps {
  id?: string
  name?: string
  className?: string
  value?: any
  placeholder?: string
  textField?: string
  disabled?: boolean
  error?: IDropdown['error']
  paddingClass?: string
  renderItem?: IDropdown['renderItem']
  renderButtonContent?: IDropdown['renderButtonContent']
  renderClearButton?: IDropdown['renderClearButton']
  onChange: IDropdown['onChange']
  onBlur?: IDropdown['onBlur']
  bgColorClass?: string
}

const theme = {
  fillAnimation:
    'animate-input-fill-motion bg-no-repeat [background-position:left_center] [background-size:0%_100%] [background-image:linear-gradient(90deg,rgb(var(--jade-50))_0%,rgb(var(--jade-50))_72%,rgb(var(--jade-100)/0.9)_86%,rgb(var(--jade-50)/0)_100%)]'
}

// eslint-disable-next-line react/display-name
const Dropdown = forwardRef<DropdownHandle, IDropdown>((props, ref) => {
  const {
    data = [],
    loading,
    disabled,
    value,
    title,
    defaultOpen,
    required,
    onCreate,
    DataLoader = Noop,
    queryVariables,
    popperPlacement,
    error = null,
    onChange,
    buttonElement,
    textField = 'name',
    searchFields,
    labelFontSize = 'text-sm',
    labelClass = 'font-bold',
    bgColorClass = '',
    renderInPortal = false
  } = props
  const {
    isAnimating,
    triggerAnimation: triggerFillAnimation,
    handleAnimationEnd
  } = useInputFillAnimation()
  const propsLoading = loading
  const propsData = data

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')

  useEffect(() => {
    if (!searchTerm.trim()) {
      setDebouncedTerm('')
      return
    }
    const t = setTimeout(() => setDebouncedTerm(searchTerm), 300)
    return () => clearTimeout(t)
  }, [searchTerm])

  const [buttonRefElement, setButtonRefElement] =
    useState<HTMLButtonElement | null>(null)
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  )

  const {
    state: popperState,
    styles: popperStyles,
    attributes: popperAttributes
  } = usePopper(referenceElement, popperElement, {
    placement: popperPlacement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 1]
        }
      }
    ]
  })

  useEffect(() => {
    if (defaultOpen && buttonRefElement) {
      buttonRefElement.click()
    }
  }, [defaultOpen, buttonRefElement])

  const onListboxChange = useCallback(
    (value) => {
      if (value.create) {
        // when create 'option' is selected
        const { create, ...rest } = value

        onCreate?.(rest)
      } else {
        onChange(value)
      }
    },
    [onCreate, onChange]
  )

  useImperativeHandle(
    ref,
    () => ({
      triggerFillAnimation
    }),
    [triggerFillAnimation]
  )

  return (
    <React.Fragment>
      <Listbox disabled={disabled} value={value} onChange={onListboxChange}>
        {({ open }) => (
          <ControlDataLoader
            DataLoader={DataLoader}
            open={open}
            variables={queryVariables}
            fetchPolicy="cache-and-network"
            searchTerm={debouncedTerm}
            searchFields={searchFields ?? [textField]}
          >
            {(
              {
                data,
                loading,
                loadMore,
                hasNextPage,
                usePagination
              }: {
                data?: any[]
                loading?: boolean
                loadMore?: () => void
                hasNextPage?: boolean
                usePagination?: boolean
              } = {
                data: propsData,
                loading: propsLoading
              }
            ) => {
              if (!open && (searchTerm || debouncedTerm)) {
                setSearchTerm('')
                setDebouncedTerm('')
              }

              const fields = searchFields?.length ? searchFields : [textField]
              const normalizedSearchTerm = toLower(searchTerm.trim())
              const filteredData =
                !usePagination && normalizedSearchTerm.length
                  ? (data?.filter((dataItem) =>
                      fields.some((field) =>
                        toLower(
                          String(dataItem[getSearchFieldName(field)] ?? '')
                        ).includes(normalizedSearchTerm)
                      )
                    ) ?? [])
                  : data

              return (
                <div className="relative">
                  {title && (
                    <Label
                      as="label"
                      className={classNames([
                        'block relative text-sm text-oxford-gray-800 pb-2',
                        labelFontSize,
                        labelClass,
                        {
                          required
                        }
                      ])}
                    >
                      {title}
                    </Label>
                  )}
                  <div ref={setReferenceElement} className="relative">
                    {buttonElement?.({
                      open,
                      loading,
                      fillAnimationClassName: isAnimating
                        ? theme.fillAnimation
                        : undefined,
                      onFillAnimationEnd: handleAnimationEnd,
                      popperPlacement: popperState?.placement ?? null,
                      setButtonElement: setButtonRefElement
                    }) ?? (
                      <DropdownButton
                        id={props.id}
                        name={props.name}
                        className={props.className}
                        value={value}
                        placeholder={props.placeholder}
                        renderItem={props.renderItem}
                        textField={textField}
                        paddingClass={props.paddingClass}
                        disabled={disabled}
                        fillAnimationClassName={
                          isAnimating ? theme.fillAnimation : undefined
                        }
                        open={open}
                        loading={loading}
                        onFillAnimationEnd={handleAnimationEnd}
                        popperPlacement={popperState?.placement ?? null}
                        error={error}
                        onChange={onChange}
                        onBlur={props.onBlur}
                        renderClearButton={props.renderClearButton}
                        renderButtonContent={props.renderButtonContent}
                        setButtonElement={setButtonRefElement}
                        bgColorClass={bgColorClass}
                      />
                    )}
                  </div>

                  <DropdownList
                    {...props}
                    searchFields={searchFields}
                    data={filteredData}
                    loading={loading}
                    setPopperElement={setPopperElement}
                    popperStyles={popperStyles}
                    popperAttributes={popperAttributes}
                    popperPlacement={popperState?.placement ?? null}
                    onSearchChange={setSearchTerm}
                    searchValue={searchTerm}
                    loadMore={loadMore}
                    hasNextPage={hasNextPage}
                    renderInPortal={renderInPortal}
                  />
                </div>
              )
            }}
          </ControlDataLoader>
        )}
      </Listbox>
      {error && <FormError error={error} />}
    </React.Fragment>
  )
})

export default Dropdown

interface IDropdownTextButton {
  id?: string
  name?: string
  className?: string
  value?: any
  placeholder?: string
  textField?: string
  disabled?: boolean
  open?: boolean
  loading?: boolean
  showArrow?: boolean
  labelClassName?: string
  setButtonElement?: (element: HTMLButtonElement | null) => void
  onBlur?: (e?: any) => void
}

export const DropdownTextButton = ({
  id = '',
  name = '',
  className = '',
  value,
  placeholder = 'Select...',
  textField = 'name',
  disabled,
  open,
  loading,
  showArrow = true,
  labelClassName = 'text-sm text-primary',
  setButtonElement,
  onBlur
}: IDropdownTextButton) => {
  const getDisplayText = () => {
    if (!value) return placeholder

    if (typeof value === 'object') {
      return value[textField] || placeholder
    }

    return String(value) || placeholder
  }

  return (
    <ListboxButton
      ref={(newRef) => {
        setButtonElement?.(newRef)
      }}
      onBlur={onBlur}
      className={classNames([
        'flex items-center gap-2 w-full text-left cursor-pointer focus:outline-none',
        {
          'opacity-50 cursor-not-allowed': disabled || loading
        },
        className
      ])}
      name={name}
      id={id}
      disabled={disabled || loading}
    >
      <TruncatedTooltipText
        text={getDisplayText()}
        className={classNames([
          'hover:text-electric-violet-600',
          labelClassName
        ])}
      />
      {showArrow && (
        <i
          className={classNames([
            'fas fa-chevron-down text-sm text-primary',
            {
              'text-oxford-gray-800': !disabled,
              'text-oxford-gray-200': disabled,
              'rotate-180': open
            }
          ])}
          aria-hidden="true"
        />
      )}
    </ListboxButton>
  )
}
